import { useCallback, useMemo } from 'react';
import { Source, Layer, Marker } from '@vis.gl/react-maplibre';
import type { MarkerDragEvent } from '@vis.gl/react-maplibre';
import type { FeatureCollection, Polygon } from 'geojson';
import { useLandmarkStore } from '../state/landmarks';

const SQRT2 = Math.SQRT2;
const METERS_PER_DEG_LAT = 111_320;
const ROTATION_HANDLE_OFFSET_M = 30;
const MIN_SHORT_SIDE_M = 50;

// Convert a local (x = width-axis, y = height-axis) offset in meters, rotated by
// `rotationDeg` clockwise (rotation=0 → width along east, height along north),
// to a world lng/lat offset relative to a reference latitude.
function localOffsetToLngLat(
  localX: number,
  localY: number,
  rotationDeg: number,
  refLat: number,
  refLng: number,
): [number, number] {
  const theta = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const dEast = localX * cos + localY * sin;
  const dNorth = -localX * sin + localY * cos;
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((refLat * Math.PI) / 180);
  return [refLng + dEast / metersPerDegLng, refLat + dNorth / METERS_PER_DEG_LAT];
}

// Inverse of the above — given a lng/lat that's near the reference, return
// the local (width, height) coordinates in meters.
function lngLatToLocalOffset(
  lng: number,
  lat: number,
  rotationDeg: number,
  refLat: number,
  refLng: number,
): [number, number] {
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((refLat * Math.PI) / 180);
  const dEast = (lng - refLng) * metersPerDegLng;
  const dNorth = (lat - refLat) * METERS_PER_DEG_LAT;
  const theta = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return [dEast * cos - dNorth * sin, dEast * sin + dNorth * cos];
}

function cornerLocalOffsets(shortSideMeters: number): Array<[number, number]> {
  const w = shortSideMeters;
  const h = shortSideMeters * SQRT2;
  return [
    [-w / 2, h / 2],
    [w / 2, h / 2],
    [w / 2, -h / 2],
    [-w / 2, -h / 2],
  ];
}

/**
 * Paper-size frame the artist can drag around the map to mark which patch of
 * Antwerp they should draw. Locked to the A-series aspect ratio (1:√2) and
 * pans/zooms with the underlying map.
 */
export function PaperFrameOverlay() {
  const visible = useLandmarkStore((state) => state.paperFrameVisible);
  const center = useLandmarkStore((state) => state.paperFrameCenter);
  const shortSideMeters = useLandmarkStore((state) => state.paperFrameShortSideMeters);
  const rotationDeg = useLandmarkStore((state) => state.paperFrameRotation);
  const opacity = useLandmarkStore((state) => state.paperFrameOpacity);
  const borderVisible = useLandmarkStore((state) => state.paperFrameBorderVisible);
  const locked = useLandmarkStore((state) => state.paperFrameLocked);
  const isAdmin = useLandmarkStore((state) => state.isAdmin);
  const serverLoaded = useLandmarkStore((state) => state.paperFrameServerLoaded);
  const setCenter = useLandmarkStore((state) => state.setPaperFrameCenter);
  const setShortSide = useLandmarkStore((state) => state.setPaperFrameShortSide);
  const setRotation = useLandmarkStore((state) => state.setPaperFrameRotation);

  const corners = useMemo<Array<[number, number]>>(() => {
    return cornerLocalOffsets(shortSideMeters).map(([x, y]) =>
      localOffsetToLngLat(x, y, rotationDeg, center[1], center[0]),
    );
  }, [center, shortSideMeters, rotationDeg]);

  const rotationHandle = useMemo<[number, number]>(() => {
    const offset = shortSideMeters * SQRT2 * 0.5 + ROTATION_HANDLE_OFFSET_M;
    return localOffsetToLngLat(0, offset, rotationDeg, center[1], center[0]);
  }, [center, shortSideMeters, rotationDeg]);

  const polygonCollection = useMemo<FeatureCollection<Polygon>>(
    () => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[...corners, corners[0]]],
          },
        },
      ],
    }),
    [corners],
  );

  const handleCenterDrag = useCallback(
    (event: MarkerDragEvent) => {
      setCenter([event.lngLat.lng, event.lngLat.lat]);
    },
    [setCenter],
  );

  const handleCornerDrag = useCallback(
    (event: MarkerDragEvent) => {
      const [lx, ly] = lngLatToLocalOffset(
        event.lngLat.lng,
        event.lngLat.lat,
        rotationDeg,
        center[1],
        center[0],
      );
      // Uniform scale from corner distance — preserves the A-series aspect ratio.
      // Half-diagonal of an A-series rectangle = (w/2) * sqrt(3).
      const distance = Math.sqrt(lx * lx + ly * ly);
      const nextShortSide = Math.max(MIN_SHORT_SIDE_M, (2 * distance) / Math.sqrt(3));
      setShortSide(nextShortSide);
    },
    [center, rotationDeg, setShortSide],
  );

  const handleRotationDrag = useCallback(
    (event: MarkerDragEvent) => {
      const metersPerDegLng =
        METERS_PER_DEG_LAT * Math.cos((center[1] * Math.PI) / 180);
      const dEast = (event.lngLat.lng - center[0]) * metersPerDegLng;
      const dNorth = (event.lngLat.lat - center[1]) * METERS_PER_DEG_LAT;
      // Local y-axis (height) at rotation=0 points north. atan2 measured from
      // north, clockwise positive: atan2(dEast, dNorth).
      const angleRad = Math.atan2(dEast, dNorth);
      let deg = (angleRad * 180) / Math.PI;
      // Normalize to (-180, 180].
      if (deg > 180) deg -= 360;
      if (deg <= -180) deg += 360;
      setRotation(deg);
    },
    [center, setRotation],
  );

  // Local toggle off → hide entirely.
  if (!visible) return null;
  // Non-admins only see the frame once it's been locked & published to the
  // server. Until then it's an admin draft and shouldn't reach the artist.
  if (!isAdmin && (!serverLoaded || !locked)) return null;
  // Handles only show for admins on an unlocked frame — locking is also the
  // "freeze" gesture for the admin themselves.
  const showHandles = isAdmin && !locked;

  const fillOpacity = Math.max(0, Math.min(1, opacity));
  const handleStrokeColor = borderVisible ? '#8b3a2a' : '#1a1410';
  const handleFillColor = '#fffdf2';

  return (
    <>
      <Source id="paper-frame" type="geojson" data={polygonCollection}>
        <Layer
          id="paper-frame-fill"
          type="fill"
          paint={{
            'fill-color': '#8b3a2a',
            'fill-opacity': fillOpacity,
          }}
        />
        {borderVisible ? (
          <Layer
            id="paper-frame-outline"
            type="line"
            paint={{
              'line-color': '#8b3a2a',
              'line-width': 2,
              'line-dasharray': [4, 3],
              'line-opacity': 0.95,
            }}
          />
        ) : null}
      </Source>

      {showHandles ? (
        <>
          {/* Center handle — drag to translate. */}
          <Marker
            longitude={center[0]}
            latitude={center[1]}
            draggable
            onDrag={handleCenterDrag}
            anchor="center"
          >
            <Handle kind="center" stroke={handleStrokeColor} fill={handleFillColor} />
          </Marker>

          {/* Corner handles — drag any to scale uniformly. */}
          {corners.map(([lng, lat], i) => (
            <Marker
              key={`corner-${i}`}
              longitude={lng}
              latitude={lat}
              draggable
              onDrag={handleCornerDrag}
              anchor="center"
            >
              <Handle kind="corner" stroke={handleStrokeColor} fill={handleFillColor} />
            </Marker>
          ))}

          {/* Rotation handle — sits beyond the top edge. */}
          <Marker
            longitude={rotationHandle[0]}
            latitude={rotationHandle[1]}
            draggable
            onDrag={handleRotationDrag}
            anchor="center"
          >
            <Handle kind="rotate" stroke={handleStrokeColor} fill={handleFillColor} />
          </Marker>
        </>
      ) : null}
    </>
  );
}

type HandleKind = 'center' | 'corner' | 'rotate';

function Handle({ kind, stroke, fill }: { kind: HandleKind; stroke: string; fill: string }) {
  const size = kind === 'center' ? 18 : kind === 'corner' ? 14 : 16;
  const cursor =
    kind === 'center' ? 'move' : kind === 'corner' ? 'nwse-resize' : 'grab';
  const title =
    kind === 'center'
      ? 'Drag to move the frame'
      : kind === 'corner'
      ? 'Drag to resize (aspect locked)'
      : 'Drag to rotate';

  return (
    <div
      title={title}
      style={{
        width: size,
        height: size,
        borderRadius: kind === 'rotate' ? '50%' : kind === 'center' ? 4 : 3,
        background: fill,
        border: `2px solid ${stroke}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        cursor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {kind === 'rotate' ? (
        <svg
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <polyline points="21 3 21 9 15 9" />
        </svg>
      ) : kind === 'center' ? (
        <svg
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="5 9 2 12 5 15" />
          <polyline points="9 5 12 2 15 5" />
          <polyline points="15 19 12 22 9 19" />
          <polyline points="19 9 22 12 19 15" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <line x1="12" y1="2" x2="12" y2="22" />
        </svg>
      ) : null}
    </div>
  );
}
